import { AfterViewInit, Component, ComponentFactoryResolver, OnDestroy, Type, ViewChild } from '@angular/core';
import { MatCalendar } from '@angular/material/datepicker';

import { Moment } from 'moment';
import * as moment from 'moment-timezone';

import { ColumnApi, GridApi, IToolPanel } from 'ag-grid-community';

import { RenderComponentDirective } from '../../../directives/render-component.directive';

import { TranslateService } from '@ngx-translate/core';
import { CalendarHeaderComponent } from '../../xs-components/calendar-header.component';
import {
  ColIdEnum,
  PeriodFrequency,
  ReportsService,
  TimeFrameValueEnums,
  TransformedSingleData,
} from '../../../services/reports.service';
import { CommonService, DaysOfTheWeekEnum } from 'src/app/services/common.service';

// Model
import { CustomToolPanelParams, GenericComponent } from '../../../model/reports';
import { Subscription } from 'rxjs';
import { TranslationsService } from '../../../services/translations.service';
import { ColumnsService } from '../../../services/columns.service';
import { convertAgGridColorToSidePaneColor } from '../../../helpers/data.helpers';

@Component({
  selector: 'vrm-custom-details-tool-pane',
  templateUrl: './custom-details-tool-pane.component.html',
  styleUrls: ['./custom-details-tool-pane.component.scss'],
})
export class CustomDetailsToolPaneComponent implements IToolPanel, AfterViewInit, OnDestroy {
  selectedRowItems: Array<TransformedSingleData> = [];
  moreDetailsComponent: Type<any>;
  selectedDate: any = moment().startOf('day');
  calendarHeader = CalendarHeaderComponent;
  selectedTimeSpan: Date | Moment | number | null = null;
  calendarFilter: (date: any) => boolean;
  dateHighlighterClass: (dateToHighlight: Date) => any;
  hideRowDetail: boolean;
  @ViewChild('timeFrameCalendar', { static: false })
  timeFrameCalendar: MatCalendar<Moment>;

  @ViewChild(RenderComponentDirective) renderDirective: RenderComponentDirective;

  timeFramePanelExpanded: boolean = true;

  private currentTimeZone: string;
  commonReportDataChangedSubscription: Subscription;
  private api: GridApi<any>;
  private columnApi: ColumnApi;

  constructor(
    private componentFactoryResolver: ComponentFactoryResolver,
    public rptService: ReportsService,
    public cService: CommonService,
    public translate: TranslateService,
    private translationsService: TranslationsService,
    private columnsService: ColumnsService
  ) {
    this.currentTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    console.log('%c Current TimeZone', 'color: yellow', this.currentTimeZone);
    this.updateFrequency();

    this.commonReportDataChangedSubscription = this.rptService.commonReportDataChanged$.subscribe({
      next: (reportMeta) => {
        setTimeout(() => {
          this.updateFrequency();
          this.updateCalendarViewToSelectedDate();
        });
      },
    });
  }

  ngAfterViewInit() {
    this.updateCalendarViewToSelectedDate();
  }

  updateCalendarViewToSelectedDate() {
    if (this.timeFrameCalendar) {
      this.timeFrameCalendar.activeDate = this.selectedDate;
    }
  }

  agInit(params: CustomToolPanelParams): void {
    this.api = params.api;
    this.columnApi = params.columnApi;
    params.api.addEventListener('rowClicked', this.refresh.bind(this));
    params.api.addEventListener('selectionChanged', this.selectionChanged.bind(this));
    params.api.addEventListener('rowDataUpdated', this.gridDataChanged.bind(this));
    if (params.moreDetails) {
      this.moreDetailsComponent = params.moreDetails;
    }
    if (params.hideRowDetail == true) {
      this.hideRowDetail = params.hideRowDetail;
    }
  }

  refresh() {
    setTimeout(() => {
      if (this.rptService.selectedRows.length === 1) {
        // TODO: Need to understand why this depends on a component just to hide / disable
        if (this.moreDetailsComponent) {
          this._loadMoreDetails(this.rptService.selectedRows[0].moreDetails);
        }

        const selectedData = this.rptService.selectedRows[0];
        const selectedItemKeys = Object.keys(selectedData);

        this.selectedRowItems = [];

        for (const key of selectedItemKeys) {
          const columnName = key;
          const columnDefinition = this.columnsService.getColumnDefinition(columnName);
          const selectedNodes = this.api.getSelectedNodes();
          const structure = this.rptService.dataStructure[columnName];

          if (columnDefinition && selectedNodes.length === 1) {
            const colDef = this.api.getColumnDef(columnName);
            const column = this.columnApi.getColumn(columnName);
            const node = selectedNodes[0];
            const data = node.data;
            const context = this.api.__getContext();
            const value = data[columnName];
            let transformedValue = value;
            let cellClass = null;

            if (colDef.refData) {
              transformedValue = colDef.refData[value];
            } else if (typeof colDef.valueFormatter === 'function') {
              transformedValue = colDef.valueFormatter({
                value,
                data,
                node,
                colDef,
                context,
                column,
                api: this.api,
                columnApi: this.columnApi,
              });
            } else if (typeof colDef.valueGetter === 'function') {
              transformedValue = colDef.valueGetter({
                node,
                data,
                column,
                colDef,
                context,
                getValue: (fieldName) => this.api.getValue(fieldName, node),
                columnApi: this.columnApi,
                api: this.api,
              });
            }

            if (typeof colDef.cellClass === 'function') {
              cellClass = colDef.cellClass({
                value,
                data,
                node,
                colDef,
                column,
                context,
                rowIndex: node.rowIndex,
                api: this.api,
                columnApi: this.columnApi,
              });
            }

            this.selectedRowItems.push({
              uiValue: transformedValue,
              uiColName: this.translationsService.translate(`REPORTCOLUMNS.${structure.name}`, structure.name),
              color: convertAgGridColorToSidePaneColor(cellClass),
            });

            continue;
          }

          if (structure) {
            const value = selectedData[columnName];

            if (value || !isNaN(value)) {
              let transformedData: TransformedSingleData = this.rptService.transformSingleDataForDetailsPane(
                columnName,
                value
              );
              transformedData = this.rptService.transformSingleDataBasedOnAdjacentFields(
                transformedData,
                columnName,
                selectedData
              );

              if (structure.columnDefinition) {
                const matchDataParameter = new RegExp(/:(\w+)/g);

                if (structure.columnDefinition.valueGetter) {
                  let uiValue = structure.columnDefinition.valueGetter;
                  let match = null;
                  const matcher = uiValue.matchAll(matchDataParameter);
                  while ((match = matcher.next())) {
                    if (match.done) {
                      break;
                    }
                    const paramTag = match.value != null ? match.value[0] : null;
                    const paramName = match.value != null ? match.value[1] : null;
                    const replaceValue =
                      paramName != null && selectedData[paramName] != null ? selectedData[paramName] : '';
                    uiValue = uiValue.replace(paramTag, replaceValue);
                  }

                  if (uiValue.toUpperCase() === 'NA' || uiValue.toLowerCase() === 'N/A') {
                    uiValue = this.cService.readyTranslations.reportData.na;
                  }

                  transformedData.uiValue = uiValue;
                }
                if (structure.columnDefinition.cellClass) {
                  const match = structure.columnDefinition.cellClass.matchAll(matchDataParameter).next().value;
                  const paramName = match != null ? match[1] : null;

                  transformedData.color =
                    paramName != null ? selectedData[paramName] : structure.columnDefinition.cellClass;
                }
              }

              this.selectedRowItems.push(transformedData);
            }
          }
        }
      }
    });
  }

  // EVENTS
  selectionChanged() {
    if (this.renderDirective) {
      this.renderDirective.viewContainer.clear();
    }

    if (this.selectedRowItems.length !== 1) {
      this.selectedRowItems = [];
    }
  }

  updateFrequency() {
    if (this.rptService.selectedTimeFrame && this.rptService.selectedTimeFrame.timeFrame) {
      if (this.rptService.selectedTimeFrame.timeFrame.period) {
        const frequency: PeriodFrequency = this.rptService.availableFrequencies.find(
          (freq) => freq.value == this.rptService.selectedTimeFrame.timeFrame.period
        );

        this.rptService.selectedTimeFrame.frequency = frequency;
        const selectedDate = new Date(this.rptService.selectedTimeFrame.timeFrame.date);

        setTimeout(() => {
          if (frequency.value === TimeFrameValueEnums.year) {
            this.switchToYearlyView(selectedDate);
          } else if (frequency.value === TimeFrameValueEnums.month) {
            this.switchToMonthlyView(selectedDate);
          } else if (frequency.value === TimeFrameValueEnums.week) {
            this.switchToWeeklyView(selectedDate);
          } else {
            this.switchToDailyView();
          }
        });
      }

      if (this.rptService.selectedTimeFrame.timeFrame.date) {
        this.selectedDate = moment.utc(
          this.rptService.selectedTimeFrame.timeFrame.date,
          'YYYY-MM-DD',
          this.cService.matLangCode
        );
      }
    }
  }

  gridDataChanged() {
    this.resetSelectedRowItem();

    if (this.rptService.selectedTimeFrame.timeFrame) {
      this.persistDefaultTimeFrame();
      console.log('%c Time Frame on Load', 'color: limegreen', this.rptService.selectedTimeFrame);
    }

    this.updateFrequency();
  }

  selectedCalendarDateChanged(selectedDate: Date, preventRefresh?: boolean) {
    if (this.timeFrameCalendar) {
      this.selectedTimeSpan = moment.utc(selectedDate, 'YYYY-MM-DD', this.cService.matLangCode);
      console.log('Selected date', this.selectedTimeSpan);

      this.timeFrameCalendar.activeDate = this.selectedTimeSpan;

      if (!preventRefresh) {
        this.collateTimeFrameSelectionAndTriggerReportRefresh();
      }
    }
  }

  selectedCalendarMonthChanged(selectedDate: Date, preventRefresh?: boolean) {
    // NOTE: This offset to 7th day on month selection ensures that week is able to find a default
    // first week in the currently selected month without defaulting to last week in previous month
    const selectedDayOffset = new Date(moment(selectedDate).year(), moment(selectedDate).month(), 7);

    this.selectedDate = this.currentTimeZone.toLowerCase().includes('europe')
      ? moment(selectedDayOffset, 'YYYY-MM-DD', this.cService.matLangCode)
      : moment.utc(selectedDayOffset, 'YYYY-MM-DD', this.cService.matLangCode);

    this.selectedTimeSpan = this.selectedDate;
    console.log('Selected month:', this.selectedDate);

    if (!preventRefresh) {
      this.collateTimeFrameSelectionAndTriggerReportRefresh();
    }
  }

  selectedCalendarYearChanged(selectedDate: Date, preventRefresh?: boolean) {
    this.selectedTimeSpan = moment.utc(selectedDate, 'YYYY-MM-DD', this.cService.matLangCode);
    this.selectedDate = moment.utc(selectedDate, 'YYYY-MM-DD', this.cService.matLangCode);

    if (!preventRefresh) {
      this.collateTimeFrameSelectionAndTriggerReportRefresh();
    }
  }

  timeFrameFrequencyChanged(frequency: PeriodFrequency, preventRefresh?: boolean) {
    this.rptService.selectedTimeFrame.frequency = frequency;
    const selectedDate = new Date(this.rptService.selectedTimeFrame.timeFrame.date);

    setTimeout(() => {
      if (frequency.value === TimeFrameValueEnums.year) {
        this.switchToYearlyView(selectedDate);

        if (!preventRefresh) {
          this.collateTimeFrameSelectionAndTriggerReportRefresh();
        }
      } else if (frequency.value === TimeFrameValueEnums.month) {
        this.switchToMonthlyView(selectedDate);

        if (!preventRefresh) {
          this.collateTimeFrameSelectionAndTriggerReportRefresh();
        }
      } else if (frequency.value === TimeFrameValueEnums.week) {
        this.switchToWeeklyView(selectedDate);

        // NOTE: In future, if default start day of the week is set from the backend,
        // it'll just be a matter of picking the correct start day. For now, use sun only.
        this.selectedCalendarDateChanged(this.selectedDate.toDate());

        // NOTE
        // Check processLanguageTranslations in common-service to see where
        // all languages are forced to Start day-of-the-week from Sunday.
      } else {
        this.switchToDailyView();
        this.selectedCalendarDateChanged(selectedDate);
      }

      // Ensures the current month of the selected date is in view
      // instead of the current date's month
      if (selectedDate && this.timeFrameCalendar) {
        this.timeFrameCalendar.activeDate = moment.utc(selectedDate);
      }

      // console.log('%c Current Selected Date', 'color: lightblue', selectedDate);
    });
  }

  collateTimeFrameSelectionAndTriggerReportRefresh() {
    const dateString: string = moment(this.selectedDate).locale('en').format('YYYY-MM-DD');

    this.rptService.selectedTimeFrame.timeFrame = {
      period: this.rptService.selectedTimeFrame.frequency.value,
      date: dateString,
    };

    console.log('Selected Time Frame', this.rptService.selectedTimeFrame.timeFrame);
    const isCommonReport = this.cService.navigatedReport.commonReportPath ? true : false;
    this.rptService.triggerServerFilterChangedObservable(isCommonReport);
  }

  persistDefaultTimeFrame() {
    if (this.rptService.hasTimeFrameFilter) {
      if (this.rptService.selectedTimeFrame.timeFrame) {
        const selectedDate = moment
          .utc(this.rptService.selectedTimeFrame.timeFrame.date, 'YYYY-MM-DD', this.cService.matLangCode)
          .toDate();

        if (this.rptService.selectedTimeFrame.frequency.value === TimeFrameValueEnums.month) {
          this.selectedCalendarMonthChanged(selectedDate, true);
          this.timeFramePanelExpanded = true;
        } else {
          this.selectedCalendarDateChanged(selectedDate, true);
          this.timeFramePanelExpanded = true;
        }
      } else {
        this.timeFramePanelExpanded = false;
      }
    }
  }

  // METHODS
  resetSelectedRowItem() {
    if (this.selectedRowItems.length !== 1) {
      this.selectedRowItems = [];
    }
  }

  private switchToWeeklyView(selectedDate: Date) {
    if (this.timeFrameCalendar) {
      this.timeFrameCalendar.currentView = 'month';

      this.calendarFilter = (date: Moment) => {
        const day = date.day();
        return day == DaysOfTheWeekEnum.sun;
      };

      this.selectedDate = moment.utc(selectedDate).startOf('week').day(DaysOfTheWeekEnum.sun);
    }
  }

  private switchToYearlyView(selectedDate: Date) {
    if (this.timeFrameCalendar) {
      this.selectedDate = moment.utc(selectedDate);
      this.timeFrameCalendar.currentView = 'multi-year';
    }
  }

  private switchToMonthlyView(selectedDate: Date) {
    if (this.timeFrameCalendar) {
      this.selectedDate = moment.utc(selectedDate);
      this.timeFrameCalendar.currentView = 'year';
    }
  }

  private switchToDailyView() {
    if (this.timeFrameCalendar) {
      this.timeFrameCalendar.currentView = 'month';
      this.calendarFilter = null;
    }
  }

  /**
   * If a moreDetailsComponent is set, this method add a more Detailed tool panel
   * @param moreDetails: Column that are not in ag grid structure
   */
  // TODO: Need to understand the purpose of this change
  _loadMoreDetails(moreDetails: any) {
    const componentFactory = this.componentFactoryResolver.resolveComponentFactory(this.moreDetailsComponent);
    this.renderDirective.viewContainer.clear();
    const componentRef = this.renderDirective.viewContainer.createComponent(componentFactory);
    (<GenericComponent>componentRef.instance).moreDetails = moreDetails;
  }

  ngOnDestroy(): void {
    if (this.commonReportDataChangedSubscription) {
      this.commonReportDataChangedSubscription.unsubscribe();
    }
  }
}
